import { labelOf, PROJECT_STATUSES, type ProjectRow } from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  FolderKanbanIcon,
  HardHatIcon,
  InboxIcon,
  PiggyBankIcon,
  PlusIcon,
  ReceiptIcon,
  TrendingUpIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { AnimatedNumber, Stagger, StaggerItem } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect, FilterTabs } from '@/components/ui/filter-bar'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { PROJECT_STATUS_VARIANT, projectOptionsQuery, projectsQuery } from '@/features/projects/api'
import { ProjectFormFields, useProjectForm } from '@/features/projects/project-form'
import { BudgetMeter } from '@/features/projects/project-visuals'
import { formatDate, formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const STATUS_FILTERS = ['open', 'active', 'paused', 'completed', 'cancelled', 'all'] as const

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(STATUS_FILTERS).optional(),
  customer: z.string().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/projects/')({
  staticData: { crumb: 'Proyectos' },
  validateSearch: searchSchema,
  component: ProjectsPage,
})

const col = columnHelper<ProjectRow>()

function ProjectsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('projects:write')
  const canAllocate = useCan('projects:allocate')
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [creating, setCreating] = useState(false)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const status = search.status ?? 'open'
  const list = useQuery(
    projectsQuery({
      search: search.q,
      status: status === 'all' ? undefined : status,
      customerId: search.customer,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const options = useQuery(projectOptionsQuery)
  const customers = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of options.data ?? [])
      if (p.customerId && p.customerName) map.set(p.customerId, p.customerName)
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [options.data])
  const kpis = list.data?.kpis

  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Proyecto',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <span className="hidden size-9 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground sm:flex">
              <HardHatIcon className="size-4" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{row.original.name}</span>
                {row.original.code && (
                  <span className="hidden shrink-0 font-mono text-muted-foreground text-xs sm:inline">
                    {row.original.code}
                  </span>
                )}
              </div>
              <div className="truncate text-muted-foreground text-xs">
                {row.original.customerName ?? 'Sin cliente'}
              </div>
            </div>
          </div>
        ),
      }),
      col.accessor('status', {
        header: 'Estado',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <Badge variant={PROJECT_STATUS_VARIANT[getValue()]} dot>
            {labelOf(PROJECT_STATUSES, getValue())}
          </Badge>
        ),
      }),
      col.accessor('consumedPct', {
        header: 'Presupuesto',
        meta: { hideOnMobile: true, className: 'w-56' },
        cell: ({ row }) =>
          row.original.budget > 0 ? (
            <div className="grid w-48 gap-1.5">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="text-muted-foreground tabular">{formatMoney(row.original.budget)}</span>
                <span
                  className={cn(
                    'font-medium tabular',
                    (row.original.consumedPct ?? 0) > 100 && 'text-destructive',
                  )}
                >
                  {(row.original.consumedPct ?? 0).toLocaleString('es-CR', { maximumFractionDigits: 1 })}%
                </span>
              </div>
              <BudgetMeter pct={row.original.consumedPct ?? 0} compact />
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">Sin presupuesto</span>
          ),
      }),
      col.accessor('cost', {
        header: 'Costo real',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="font-medium">{formatMoney(getValue())}</span>,
      }),
      col.accessor('grandTotal', {
        header: 'Gran total',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground">{formatMoney(getValue())}</span>,
      }),
      col.accessor('endDate', {
        header: 'Plazo',
        meta: { hideOnMobile: true },
        cell: ({ row }) =>
          row.original.startDate || row.original.endDate ? (
            <div className="text-xs">
              <div className="tabular">{formatDate(row.original.startDate, 'd MMM yy')}</div>
              <div className="text-muted-foreground tabular">
                → {formatDate(row.original.endDate, 'd MMM yy')}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          ),
      }),
    ],
    [],
  )

  const filtered = !!(search.q || search.customer || (search.status && search.status !== 'open'))

  return (
    <Page>
      <PageHeader
        title="Proyectos"
        description="Obras con presupuesto: su costo sale de las facturas recibidas y los gastos que les imputás."
        actions={
          <>
            {canAllocate && (
              <Button variant="outline" onClick={() => navigate({ to: '/projects/unassigned' })}>
                <InboxIcon /> Por imputar
              </Button>
            )}
            {canWrite && (
              <Button onClick={() => setCreating(true)}>
                <PlusIcon /> Nuevo proyecto
              </Button>
            )}
          </>
        }
      />

      <Stagger className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Presupuesto"
          icon={PiggyBankIcon}
          value={kpis?.budget}
          hint={
            kpis ? `${kpis.count} proyecto${kpis.count === 1 ? '' : 's'} · ${kpis.open} abiertos` : undefined
          }
        />
        <Kpi
          label="Costo real"
          icon={ReceiptIcon}
          value={kpis?.cost}
          accent
          hint={
            kpis && kpis.budget > 0
              ? `${((kpis.cost / kpis.budget) * 100).toLocaleString('es-CR', { maximumFractionDigits: 1 })}% del presupuesto`
              : 'Facturas, gastos y partidas'
          }
        />
        <Kpi label="Gran total" icon={TrendingUpIcon} value={kpis?.grandTotal} hint="Costo real + utilidad" />
        <Kpi
          label="Sobre presupuesto"
          icon={AlertTriangleIcon}
          count={kpis?.overBudget}
          hint={kpis?.overBudget ? 'Revisá su consumo' : 'Todos dentro del presupuesto'}
          danger={!!kpis?.overBudget}
        />
      </Stagger>

      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(p) => p.id}
        onRowClick={(p) => navigate({ to: '/projects/$id', params: { id: p.id } })}
        isRowMuted={(p) => p.status === 'cancelled'}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={status}
              onValueChange={(v) => setSearch({ status: v === 'open' ? undefined : v, page: undefined })}
              options={[
                { value: 'open', label: 'Abiertos' },
                { value: 'active', label: 'Activos' },
                { value: 'paused', label: 'En pausa' },
                { value: 'completed', label: 'Terminados' },
                { value: 'cancelled', label: 'Cancelados' },
                { value: 'all', label: 'Todos' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por proyecto o cliente…" />
            <FilterSelect
              label="Cliente"
              width="xl"
              value={search.customer ?? 'all'}
              onValueChange={(v) => setSearch({ customer: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todos los clientes' },
                ...customers.map(([id, name]) => ({ value: id, label: name })),
              ]}
            />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setSearch({ q: undefined, status: undefined, customer: undefined, page: undefined })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={FolderKanbanIcon}
            title={filtered ? 'Sin proyectos con esos filtros' : 'Todavía no hay proyectos'}
            description={
              filtered
                ? 'Probá con otro estado o limpiá la búsqueda.'
                : 'Creá una obra con su presupuesto y empezá a imputarle facturas recibidas y gastos.'
            }
            action={
              canWrite &&
              !filtered && (
                <Button onClick={() => setCreating(true)}>
                  <PlusIcon /> Nuevo proyecto
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <NewProjectSheet open={creating} onOpenChange={setCreating} />
    </Page>
  )
}

function Kpi({
  label,
  icon: Icon,
  value,
  count,
  hint,
  accent,
  danger,
}: {
  label: string
  icon: typeof ReceiptIcon
  value?: number
  count?: number
  hint?: string
  accent?: boolean
  danger?: boolean
}) {
  const loading = value === undefined && count === undefined
  return (
    <StaggerItem>
      <Card className="relative h-full overflow-hidden p-5">
        {accent && (
          <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/20 blur-3xl" />
        )}
        <div className="relative">
          <div className="flex items-center justify-between text-muted-foreground text-sm">
            {label}
            <span
              className={cn(
                'flex size-8 items-center justify-center rounded-lg',
                danger
                  ? 'bg-destructive-soft text-destructive-soft-foreground'
                  : accent
                    ? 'bg-warning-soft text-warning-soft-foreground'
                    : 'bg-muted',
              )}
            >
              <Icon className="size-4" />
            </span>
          </div>
          {loading ? (
            <Skeleton className="mt-3 h-8 w-40" />
          ) : (
            <div className="mt-2 truncate font-semibold text-[26px] tracking-tight tabular">
              {count !== undefined ? (
                <AnimatedNumber value={count} format={(n) => String(Math.round(n))} />
              ) : (
                <AnimatedNumber value={value ?? 0} format={formatMoney} />
              )}
            </div>
          )}
          {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
        </div>
      </Card>
    </StaggerItem>
  )
}

function NewProjectSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const { form, save, submit } = useProjectForm(null, (project) => {
    onOpenChange(false)
    navigate({ to: '/projects/$id', params: { id: project.id }, search: { tab: 'phases' } })
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: se limpia al abrir
  useEffect(() => {
    if (open) form.reset()
  }, [open])
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="xl">
        <form onSubmit={submit} noValidate className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>Nuevo proyecto</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <ProjectFormFields form={form} />
          </SheetBody>
          <SheetFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={save.isPending}>
              Crear proyecto
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
